/**
 * The Staff App's single seam onto the network.
 *
 * TRANSPORT: `createApiClient` from `@sonauto/api-client`. That package owns
 * the things no app may reimplement — the single-flight refresh lock, the
 * idempotency key that survives a 401 replay, the typed `ApiError`, the offline
 * short-circuit. No screen in this app calls `fetch`.
 *
 * ENDPOINTS: defined here, not taken from `@sonauto/api-client`'s `staffApi`.
 * That helper currently exposes six GETs typed as `Record<string, unknown>` and
 * none of the mutations this app is built on (attendance, accept/reject/start/
 * complete, scan, appeals, leave, swaps, advances, presence). Rather than cast
 * `unknown` at forty call sites, the staff surface is declared once here
 * against `contracts.ts`, which is transcribed from routes/staff.ts. When the
 * shared `staffApi` grows those endpoints this file becomes a re-export.
 *
 * Header contract, verified against apps/api-worker/src/middleware/index.ts:
 *   authorization    handled by the client
 *   idempotency-key  auto on every mutation; passed explicitly when replaying
 *                    a queued offline write so the retry carries the SAME key
 *   x-action-reason  `requireReason` on /tasks/:id/reject reads the HEADER
 *   x-device-id      not sent by the shared client, added here — the server's
 *                    UNREGISTERED_DEVICE / SHARED_DEVICE risk flags need it
 */
import { createApiClient, type ApiClient, type RequestOptions } from '@sonauto/api-client';
import type {
  AppealCreateResponse,
  AppealRequest,
  AppealsResponse,
  AttendanceHistoryResponse,
  AttendanceRequest,
  AttendanceResponse,
  ColleaguesResponse,
  HomeResponse,
  InboxNotificationsResponse,
  KpiPeriodType,
  KpiResponse,
  LeaveRequestBody,
  LeaveRequestResponse,
  LeaveRequestsResponse,
  LoginResponse,
  MeResponse,
  MessageSendResponse,
  MessagesResponse,
  NotificationReadResponse,
  NotificationsReadAllResponse,
  OpenShiftsResponse,
  PayrollPreview,
  RealtimeTokenResponse,
  RejectReasonCode,
  SalaryAdvanceBody,
  SalaryAdvanceResponse,
  SalaryAdvancesResponse,
  ScanRequest,
  ScanResponse,
  ShiftSwapBody,
  ShiftSwapResponse,
  ShiftSwapsResponse,
  ShiftsResponse,
  StaffNotificationsResponse,
  StatusRequest,
  StatusResponse,
  TaskAcceptResponse,
  TaskCompleteRequest,
  TaskCompleteResponse,
  TaskHistoryResponse,
  TaskRejectResponse,
  TaskStartResponse,
  TasksResponse,
} from './contracts';
import { clearTokens, deviceId, readTokens, writeTokens } from './session-store';
import { markSyncSettled } from './sync-status';

export { ApiError, isApiError, newIdempotencyKey } from '@sonauto/api-client';
export type { ApiErrorCode } from '@sonauto/api-client';

/**
 * `/api` because every path in `@sonauto/api-client` is versioned (`/v1/...`).
 * In dev this hits the Vite proxy on 5175 → api-worker on 8787.
 */
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';
const BASE_URL = API_BASE_URL;

/** Set by the auth provider so a dead session can bounce the user to /login. */
let unauthenticatedHandler: () => void = () => undefined;

export function setUnauthenticatedHandler(handler: () => void): void {
  unauthenticatedHandler = handler;
}

export const apiClient: ApiClient = createApiClient({
  baseUrl: BASE_URL,
  getTokens: () => readTokens(),
  setTokens: (tokens) => {
    if (tokens === null) clearTokens();
    else writeTokens(tokens);
  },
  onUnauthenticated: () => unauthenticatedHandler(),
  clientVersion: 'staff-app/0.1.0',
  // Phones on restaurant wifi are slow but not THAT slow; a shorter timeout
  // means the offline outbox takes over sooner instead of holding a spinner.
  timeoutMs: 15_000,
  onRequestSettled: (info) => markSyncSettled(info.error === null),
});

/** Every request carries the stable install id the anti-fraud signals need. */
function withDevice(options: RequestOptions = {}): RequestOptions {
  const id = deviceId();
  if (id === null) return options;
  return { ...options, headers: { ...(options.headers ?? {}), 'x-device-id': id } };
}

const get = <T>(path: string, options?: RequestOptions): Promise<T> =>
  apiClient.get<T>(path, withDevice(options));
const post = <T>(path: string, options?: RequestOptions): Promise<T> =>
  apiClient.post<T>(path, withDevice(options));

const seg = (value: string): string => encodeURIComponent(value);

// ---------------------------------------------------------------------------
// The staff surface. Screens use these; nothing else touches `apiClient`.
// ---------------------------------------------------------------------------

export const api = {
  // --- auth ---------------------------------------------------------------
  login: (input: {
    tenantCode: string;
    username: string;
    password: string;
    branchId?: string | null;
    turnstileToken?: string | null;
  }): Promise<LoginResponse> =>
    post<LoginResponse>('/v1/auth/login', {
      anonymous: true,
      // Login is not idempotency-keyed: the API's `rateLimit` path does not
      // accept the header and a replayed login is harmless anyway.
      idempotent: false,
      body: {
        tenantCode: input.tenantCode,
        username: input.username,
        password: input.password,
        // Stamps the session STAFF so the API knows which of the three PWAs
        // is calling and can scope the session accordingly.
        app: 'STAFF',
        branchId: input.branchId ?? null,
        // NEVER send the fabricated install id here. The login-body `deviceId`
        // is the REGISTERED-DEVICE authorization field: the server rejects any
        // value that is not an ACTIVE row in `devices`, so sending our random
        // localStorage id would lock every fresh install out of login forever.
        // Staff phones are personal devices — they are not registered. The
        // install id still reaches the server on every request via the
        // `x-device-id` header (see `withDevice`), which is all the
        // SHARED_DEVICE / UNREGISTERED_DEVICE anti-fraud signals consume.
        deviceId: null,
        turnstileToken: input.turnstileToken ?? null,
      },
    }),

  me: (): Promise<MeResponse> => get<MeResponse>('/v1/auth/me'),

  logout: (): Promise<{ ok: true }> => post<{ ok: true }>('/v1/auth/logout', { idempotent: false }),

  switchBranch: (branchId: string): Promise<{ ok: true; activeBranchId: string }> =>
    post('/v1/auth/switch-branch', { body: { branchId }, idempotent: false }),

  // --- home ---------------------------------------------------------------
  home: (signal?: AbortSignal): Promise<HomeResponse> => get<HomeResponse>('/v1/staff/home', { signal }),

  // --- attendance ----------------------------------------------------------
  /**
   * `clientEventId` is reused as the idempotency key so a replay of the exact
   * same tap — from the offline outbox, or from a 401 retry — is the SAME
   * logical request and the server's unique index rejects the duplicate
   * instead of paying for it twice.
   */
  attendance: (body: AttendanceRequest): Promise<AttendanceResponse> =>
    post<AttendanceResponse>('/v1/staff/attendance', {
      body,
      idempotencyKey: body.clientEventId,
    }),

  attendanceHistory: (
    query: { from?: string; to?: string; limit?: number } = {},
    signal?: AbortSignal,
  ): Promise<AttendanceHistoryResponse> =>
    get<AttendanceHistoryResponse>('/v1/staff/attendance', { query, signal }),

  // --- presence ------------------------------------------------------------
  setStatus: (body: StatusRequest): Promise<StatusResponse> =>
    post<StatusResponse>('/v1/staff/status', { body, idempotent: false }),

  // --- tasks ---------------------------------------------------------------
  tasks: (signal?: AbortSignal): Promise<TasksResponse> => get<TasksResponse>('/v1/staff/tasks', { signal }),

  acceptTask: (taskId: string, idempotencyKey: string): Promise<TaskAcceptResponse> =>
    post<TaskAcceptResponse>(`/v1/tasks/${seg(taskId)}/accept`, { idempotencyKey }),

  rejectTask: (taskId: string, reasonCode: RejectReasonCode, reason: string): Promise<TaskRejectResponse> =>
    post<TaskRejectResponse>(`/v1/tasks/${seg(taskId)}/reject`, { body: { reasonCode }, reason }),

  startTask: (taskId: string, idempotencyKey: string): Promise<TaskStartResponse> =>
    post<TaskStartResponse>(`/v1/tasks/${seg(taskId)}/start`, { idempotencyKey }),

  completeTask: (
    taskId: string,
    body: TaskCompleteRequest,
    idempotencyKey: string,
  ): Promise<TaskCompleteResponse> =>
    post<TaskCompleteResponse>(`/v1/tasks/${seg(taskId)}/complete`, { body, idempotencyKey }),

  // --- scan ----------------------------------------------------------------
  /**
   * The scan's own `nonce` is the server-side dedupe key, so this call is sent
   * WITHOUT an idempotency key: the endpoint has no `idempotent()` middleware
   * and dedupes on `task_events (tenant_id, idempotency_key)` instead.
   */
  scan: (body: ScanRequest): Promise<ScanResponse> =>
    post<ScanResponse>('/v1/staff/scan', { body, idempotent: false }),

  // --- KPI -----------------------------------------------------------------
  kpi: (periodType: KpiPeriodType, periodKey?: string, signal?: AbortSignal): Promise<KpiResponse> =>
    get<KpiResponse>('/v1/staff/kpi', { query: { periodType, periodKey }, signal }),

  appeals: (signal?: AbortSignal): Promise<AppealsResponse> =>
    get<AppealsResponse>('/v1/staff/kpi/appeals', { signal }),

  createAppeal: (body: AppealRequest): Promise<AppealCreateResponse> =>
    post<AppealCreateResponse>('/v1/staff/kpi/appeals', { body }),

  // --- shifts / leave / swap / advance / payroll ----------------------------
  shifts: (query: { from?: string; to?: string } = {}, signal?: AbortSignal): Promise<ShiftsResponse> =>
    get<ShiftsResponse>('/v1/staff/shifts', { query, signal }),

  createLeaveRequest: (body: LeaveRequestBody): Promise<LeaveRequestResponse> =>
    post<LeaveRequestResponse>('/v1/staff/leave-requests', { body }),

  createShiftSwap: (body: ShiftSwapBody): Promise<ShiftSwapResponse> =>
    post<ShiftSwapResponse>('/v1/staff/shift-swaps', { body }),

  createSalaryAdvance: (body: SalaryAdvanceBody): Promise<SalaryAdvanceResponse> =>
    post<SalaryAdvanceResponse>('/v1/staff/salary-advances', { body }),

  payrollPreview: (signal?: AbortSignal): Promise<PayrollPreview> =>
    get<PayrollPreview>('/v1/staff/payroll/preview', { signal }),

  // --- history GETs (routes/staff.ts, all SELF-scoped server-side) ----------
  taskHistory: (
    query: { status?: string; from?: number; to?: number; limit?: number } = {},
    signal?: AbortSignal,
  ): Promise<TaskHistoryResponse> => get<TaskHistoryResponse>('/v1/staff/task-history', { query, signal }),

  leaveRequests: (signal?: AbortSignal): Promise<LeaveRequestsResponse> =>
    get<LeaveRequestsResponse>('/v1/staff/leave-requests', { signal }),

  salaryAdvances: (signal?: AbortSignal): Promise<SalaryAdvancesResponse> =>
    get<SalaryAdvancesResponse>('/v1/staff/salary-advances', { signal }),

  shiftSwaps: (signal?: AbortSignal): Promise<ShiftSwapsResponse> =>
    get<ShiftSwapsResponse>('/v1/staff/shift-swaps', { signal }),

  openShifts: (branchId?: string, signal?: AbortSignal): Promise<OpenShiftsResponse> =>
    get<OpenShiftsResponse>('/v1/staff/open-shifts', { query: { branchId }, signal }),

  colleagues: (shiftId?: string, signal?: AbortSignal): Promise<ColleaguesResponse> =>
    get<ColleaguesResponse>('/v1/staff/colleagues', { query: { shiftId }, signal }),

  // --- notifications --------------------------------------------------------
  notifications: (
    query: { unreadOnly?: boolean; limit?: number } = {},
    signal?: AbortSignal,
  ): Promise<StaffNotificationsResponse> =>
    get<StaffNotificationsResponse>('/v1/staff/notifications', { query, signal }),

  /** No `idempotent()` middleware on this route; marking read twice is a no-op anyway. */
  markNotificationRead: (deliveryId: string): Promise<NotificationReadResponse> =>
    post<NotificationReadResponse>(`/v1/staff/notifications/${seg(deliveryId)}/read`, { idempotent: false }),

  /** The generic router's read-all — the staff router has no equivalent. */
  markAllNotificationsRead: (): Promise<NotificationsReadAllResponse> =>
    post<NotificationsReadAllResponse>('/v1/notifications/read-all', { idempotent: false }),

  /**
   * The generic inbox, which unlike `/staff/notifications` carries
   * `fromUserId` — the only place the app can learn a chat partner's USER id
   * (see ChatScreen for why that matters).
   */
  inbox: (query: { limit?: number } = {}, signal?: AbortSignal): Promise<InboxNotificationsResponse> =>
    get<InboxNotificationsResponse>('/v1/notifications', { query, signal }),

  // --- 1-1 chat (routes/notifications.ts, on the notifications backbone) ----
  messages: (withUserId: string, signal?: AbortSignal): Promise<MessagesResponse> =>
    get<MessagesResponse>('/v1/messages', { query: { withUserId, limit: 100 }, signal }),

  sendMessage: (toUserId: string, body: string): Promise<MessageSendResponse> =>
    post<MessageSendResponse>('/v1/messages', { body: { toUserId, body }, idempotent: false }),

  // --- realtime -------------------------------------------------------------
  realtimeToken: (branchId: string, topics: readonly string[]): Promise<RealtimeTokenResponse> =>
    post<RealtimeTokenResponse>('/v1/realtime/token', {
      body: { branchId, topics },
      idempotent: false,
    }),
} as const;
